import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  Search,
  MoreVertical,
  Eye,
  Calendar,
  Zap,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Bot,
  Phone,
  Link2,
  Unlink,
  ChevronDown,
  X,
  AlertCircle,
  ShoppingCart,
  Info,
  Clock,
} from "lucide-react";
import {
  getFlows,
  deleteFlow,
  toggleFlowStatus,
  allocateFlowToPhone,
  deallocateFlowFromPhone,
  getFlowsByPhoneId,
} from "../apis/flowAPI";
import {
  fetchAiPhoneSettings,
  toggleAiForPhone,
  updateAiKnowledgeBase,
  updateAiChatbotSettings,
} from "../apis/aiChatbotAPI";
import AiChatbotModal from "../components/flowList/AiChatbotModal";
import { checkFlowAccess } from "../apis/subscription";
import { toast } from "react-toastify";
import AddonModal from "../components/AddonModal";
import PhoneSelector from "../components/PhoneSelector";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";

const FlowList = () => {
  const navigate = useNavigate();
  const { selectedPhoneAccount } = usePhoneAccount();

  // Tab state
  const [activeTab, setActiveTab] = useState("manage");

  // Existing states for Manage Flows
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // AI Chatbot Modal states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPhoneOptions, setAiPhoneOptions] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState("");
  const [aiToggleLoading, setAiToggleLoading] = useState(false);
  const [aiKnowledgeDraft, setAiKnowledgeDraft] = useState("");
  const [toneDraft, setToneDraft] = useState("");
  const [outOfContextDraft, setOutOfContextDraft] = useState("");
  const [languageDraft, setLanguageDraft] = useState("");
  const [roleDraft, setRoleDraft] = useState("");
  const [tasksAndRulesDraft, setTasksAndRulesDraft] = useState("");
  const [aiReplyDays, setAiReplyDays] = useState([]);
  const [aiReplyStartTime, setAiReplyStartTime] = useState("");
  const [aiReplyEndTime, setAiReplyEndTime] = useState("");
  const [aiTimezone, setAiTimezone] = useState("Asia/Kolkata");
  const [aiReplyToButtons, setAiReplyToButtons] = useState(false);
  const [aiReplyToAll, setAiReplyToAll] = useState(false);
  const [aiButtonTexts, setAiButtonTexts] = useState([]);
  const [knowledgeSaving, setKnowledgeSaving] = useState(false);
  const [aiListLoading, setAiListLoading] = useState(false);

  // Allocate Flows tab states (uses PhoneAccountContext for phone selection)
  const [allocatedFlows, setAllocatedFlows] = useState([]);
  const [allocatedLoading, setAllocatedLoading] = useState(false);
  const [allFlows, setAllFlows] = useState([]);
  const [showAddFlowDropdown, setShowAddFlowDropdown] = useState(false);
  const [allocatingFlowId, setAllocatingFlowId] = useState(null);
  const [deallocatingFlowId, setDeallocatingFlowId] = useState(null);

  // Flow access states
  const [flowAccess, setFlowAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [addonPlans, setAddonPlans] = useState([]);
  const [processingAddon, setProcessingAddon] = useState(null);

  useEffect(() => {
    fetchFlows();
    checkFlowAccessStatus();
  }, [filter]);

  const checkFlowAccessStatus = async () => {
    try {
      setCheckingAccess(true);
      const response = await checkFlowAccess();
      if (response.data.success) {
        setFlowAccess(response.data.data);
      }
    } catch (error) {
      console.error("Error checking flow access:", error);
    } finally {
      setCheckingAccess(false);
    }
  };

  useEffect(() => {
    loadAiPhones();
  }, []);

  useEffect(() => {
    if (activeTab === "allocate" && allFlows.length === 0) {
      getFlows({}).then((res) => setAllFlows(res.data.flows || [])).catch(() => {});
    }
  }, [activeTab]);

  useEffect(() => {
    const waAccountId = selectedPhoneAccount?.id;
    if (activeTab === "allocate" && waAccountId) {
      fetchAllocatedFlows(waAccountId);
    }
  }, [selectedPhoneAccount?.id, activeTab]);

  useEffect(() => {
    if (!aiModalOpen || aiPhoneOptions.length === 0) {
      return;
    }
    if (!selectedPhoneId) {
      const first = aiPhoneOptions[0];
      if (first) {
        setSelectedPhoneId(first.phone_id);
        loadPhoneData(first);
      }
    }
  }, [aiModalOpen, aiPhoneOptions, selectedPhoneId]);

  const loadPhoneData = (phone) => {
    setAiKnowledgeDraft(phone.knowledge_base || "");
    setToneDraft(phone.tone || "");
    setOutOfContextDraft(phone.out_of_context_reply || "");
    setLanguageDraft(phone.language || "");
    setRoleDraft(phone.role || "");
    setTasksAndRulesDraft(phone.tasks_and_rules || "");
    setAiReplyDays(phone.ai_reply_days || []);
    setAiReplyStartTime(phone.ai_reply_start_time || "");
    setAiReplyEndTime(phone.ai_reply_end_time || "");
    setAiTimezone(phone.ai_timezone || "Asia/Kolkata");
    setAiReplyToButtons(phone.ai_reply_to_buttons || false);
    setAiReplyToAll(phone.ai_reply_to_all || false);
    // Ensure ai_button_texts is always an array
    const buttonTexts = phone.ai_button_texts;
    if (Array.isArray(buttonTexts)) {
      setAiButtonTexts(buttonTexts);
    } else if (typeof buttonTexts === 'string' && buttonTexts.trim()) {
      // Handle comma-separated string (backward compatibility)
      setAiButtonTexts(buttonTexts.split(',').map(t => t.trim()).filter(t => t));
    } else {
      setAiButtonTexts([]);
    }
  };

  const filteredFlows = flows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (flow.description &&
        flow.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedPhone = useMemo(
    () => aiPhoneOptions.find((option) => option.phone_id === selectedPhoneId),
    [aiPhoneOptions, selectedPhoneId]
  );

  const unallocatedFlows = useMemo(() => {
    const allocatedIds = allocatedFlows.map((f) => f.id);
    return allFlows.filter((flow) => !allocatedIds.includes(flow.id));
  }, [allFlows, allocatedFlows]);

  const fetchFlows = async () => {
    try {
      setLoading(true);
      const params = filter !== "all" ? { isActive: filter === "active" } : {};
      const response = await getFlows(params);
      setFlows(response.data.flows);
    } catch (error) {
      console.error("Error fetching flows:", error);
      const errorResponse = error.response?.data;
      // Don't show error for subscription issues - SubscriptionGate handles it
      if (!errorResponse?.requiresSubscription) {
        toast.error(errorResponse?.error || "Failed to load flows");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAiPhones = async () => {
    try {
      setAiListLoading(true);
      const response = await fetchAiPhoneSettings();
      const list = response.data?.data || [];
      setAiPhoneOptions(list);
      if (list.length > 0 && !selectedPhoneId) {
        setSelectedPhoneId(list[0].phone_id);
        loadPhoneData(list[0]);
      }
    } catch (error) {
      console.error("Error loading AI phone settings:", error);
    } finally {
      setAiListLoading(false);
    }
  };

  const fetchAllocatedFlows = async (waAccountId) => {
    try {
      setAllocatedLoading(true);
      const response = await getFlowsByPhoneId(waAccountId);
      setAllocatedFlows(response.data.flows || []);
    } catch (error) {
      console.error("Error fetching allocated flows:", error);
      setAllocatedFlows([]);
    } finally {
      setAllocatedLoading(false);
    }
  };

  const handleAllocateFlow = async (flowId) => {
    const waAccountId = selectedPhoneAccount?.id;
    if (!waAccountId) return;
    try {
      setAllocatingFlowId(flowId);
      await allocateFlowToPhone(flowId, waAccountId);
      await fetchAllocatedFlows(waAccountId);
      setShowAddFlowDropdown(false);
    } catch (error) {
      console.error("Error allocating flow:", error);
      alert("Failed to allocate flow");
    } finally {
      setAllocatingFlowId(null);
    }
  };

  const handleDeallocateFlow = async (flowId) => {
    const waAccountId = selectedPhoneAccount?.id;
    if (!waAccountId) return;
    if (
      !window.confirm(
        "Are you sure you want to remove this flow from this phone?"
      )
    )
      return;
    try {
      setDeallocatingFlowId(flowId);
      await deallocateFlowFromPhone(flowId, waAccountId);
      await fetchAllocatedFlows(waAccountId);
    } catch (error) {
      console.error("Error deallocating flow:", error);
      alert("Failed to remove flow");
    } finally {
      setDeallocatingFlowId(null);
    }
  };

  const handleAiToggle = async (nextValue) => {
    if (!selectedPhone) return;
    if (nextValue && !aiKnowledgeDraft.trim()) {
      const confirmed = window.confirm(
        "Warning: No knowledge base has been provided. The AI chatbot may not function properly without a knowledge base. Do you want to enable it anyway? You can add the knowledge base later."
      );
      if (!confirmed) {
        return;
      }
    }
    try {
      setAiToggleLoading(true);
      await toggleAiForPhone(selectedPhone.phone_id, nextValue);
      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? { ...option, ai_enabled: nextValue }
            : option
        )
      );
    } catch (error) {
      console.error("Error toggling AI chatbot:", error);
      alert("Failed to update AI chatbot setting");
    } finally {
      setAiToggleLoading(false);
    }
  };

  const handleKnowledgeSave = async () => {
    if (!selectedPhone) return;
    try {
      setKnowledgeSaving(true);
      await updateAiKnowledgeBase(selectedPhone.phone_id, aiKnowledgeDraft);
      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? { ...option, knowledge_base: aiKnowledgeDraft }
            : option
        )
      );
      alert("Knowledge base saved successfully");
    } catch (error) {
      console.error("Error saving knowledge base:", error);
      alert("Failed to save knowledge base");
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleOpenAiModal = async () => {
    if (aiPhoneOptions.length === 0) {
      await loadAiPhones();
    }
    setAiModalOpen(true);
  };

  const handleSelectPhone = (phoneId) => {
    setSelectedPhoneId(phoneId);
    const next = aiPhoneOptions.find((option) => option.phone_id === phoneId);
    if (next) {
      loadPhoneData(next);
    }
  };

  const handleSaveAllSettings = async (updates = {}) => {
    if (!selectedPhone) return;
    try {
      setKnowledgeSaving(true);
      const settingsToSave = {
        knowledge_base:
          updates.knowledge_base !== undefined
            ? updates.knowledge_base
            : aiKnowledgeDraft,
        tone: updates.tone !== undefined ? updates.tone : toneDraft,
        out_of_context_reply:
          updates.out_of_context_reply !== undefined
            ? updates.out_of_context_reply
            : outOfContextDraft,
        language:
          updates.language !== undefined ? updates.language : languageDraft,
        role: updates.role !== undefined ? updates.role : roleDraft,
        tasks_and_rules:
          updates.tasks_and_rules !== undefined
            ? updates.tasks_and_rules
            : tasksAndRulesDraft,
        ai_reply_days:
          updates.ai_reply_days !== undefined
            ? updates.ai_reply_days
            : aiReplyDays,
        ai_reply_start_time:
          updates.ai_reply_start_time !== undefined
            ? updates.ai_reply_start_time
            : aiReplyStartTime,
        ai_reply_end_time:
          updates.ai_reply_end_time !== undefined
            ? updates.ai_reply_end_time
            : aiReplyEndTime,
        ai_timezone:
          updates.ai_timezone !== undefined ? updates.ai_timezone : aiTimezone,
        ai_reply_to_buttons:
          updates.ai_reply_to_buttons !== undefined
            ? updates.ai_reply_to_buttons
            : aiReplyToButtons,
        ai_reply_to_all:
          updates.ai_reply_to_all !== undefined
            ? updates.ai_reply_to_all
            : aiReplyToAll,
        ai_button_texts:
          updates.ai_button_texts !== undefined
            ? updates.ai_button_texts
            : aiButtonTexts,
      };

      await updateAiChatbotSettings(selectedPhone.phone_id, settingsToSave);

      if (updates.knowledge_base !== undefined)
        setAiKnowledgeDraft(updates.knowledge_base);
      if (updates.tone !== undefined) setToneDraft(updates.tone);
      if (updates.out_of_context_reply !== undefined)
        setOutOfContextDraft(updates.out_of_context_reply);
      if (updates.language !== undefined) setLanguageDraft(updates.language);
      if (updates.role !== undefined) setRoleDraft(updates.role);
      if (updates.tasks_and_rules !== undefined)
        setTasksAndRulesDraft(updates.tasks_and_rules);
      if (updates.ai_reply_days !== undefined)
        setAiReplyDays(updates.ai_reply_days);
      if (updates.ai_reply_start_time !== undefined)
        setAiReplyStartTime(updates.ai_reply_start_time);
      if (updates.ai_reply_end_time !== undefined)
        setAiReplyEndTime(updates.ai_reply_end_time);
      if (updates.ai_timezone !== undefined) setAiTimezone(updates.ai_timezone);
      if (updates.ai_reply_to_buttons !== undefined)
        setAiReplyToButtons(updates.ai_reply_to_buttons);
      if (updates.ai_reply_to_all !== undefined)
        setAiReplyToAll(updates.ai_reply_to_all);
      if (updates.ai_button_texts !== undefined)
        setAiButtonTexts(updates.ai_button_texts);

      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? {
                ...option,
                knowledge_base: settingsToSave.knowledge_base,
                tone: settingsToSave.tone,
                out_of_context_reply: settingsToSave.out_of_context_reply,
                language: settingsToSave.language,
                role: settingsToSave.role,
                tasks_and_rules: settingsToSave.tasks_and_rules,
                ai_reply_days: settingsToSave.ai_reply_days,
                ai_reply_start_time: settingsToSave.ai_reply_start_time,
                ai_reply_end_time: settingsToSave.ai_reply_end_time,
                ai_timezone: settingsToSave.ai_timezone,
                ai_reply_to_buttons: settingsToSave.ai_reply_to_buttons,
                ai_reply_to_all: settingsToSave.ai_reply_to_all,
                ai_button_texts: settingsToSave.ai_button_texts,
              }
            : option
        )
      );
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings");
      throw error;
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm("Are you sure you want to delete this flow?")) return;

    try {
      await deleteFlow(flowId);
      alert("Flow deleted successfully");
      fetchFlows();
    } catch (error) {
      console.error("Error deleting flow:", error);
      alert("Failed to delete flow");
    }
  };

  const handleToggleStatus = async (flowId, currentStatus) => {
    try {
      await toggleFlowStatus(flowId, !currentStatus);
      alert(
        `Flow ${!currentStatus ? "activated" : "deactivated"} successfully`
      );
      fetchFlows();
    } catch (error) {
      console.error("Error toggling flow status:", error);
      alert("Failed to update flow status");
    }
  };

  const getTriggerBadge = (triggerType) => {
    const badges = {
      keyword: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        label: "Keyword",
        border: "border-blue-200",
      },
      welcome: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        label: "Welcome",
        border: "border-emerald-200",
      },
      menu: {
        bg: "bg-purple-50",
        text: "text-purple-700",
        label: "Menu",
        border: "border-purple-200",
      },
      default: {
        bg: "bg-gray-50",
        text: "text-gray-700",
        label: "Default",
        border: "border-gray-200",
      },
      api: {
        bg: "bg-amber-50",
        text: "text-amber-700",
        label: "API",
        border: "border-amber-200",
      },
    };
    const badge = badges[triggerType] || badges.default;
    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-bold ${badge.bg} ${badge.text} border ${badge.border}`}
      >
        {badge.label}
      </span>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, duration: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen bg-[#fff] p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <PhoneSelector section="flows" />
        </div>
        {/* Header Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Chatbot Flows</h1>
              <p className="text-sm text-gray-600 mt-1">
                Create and manage automated conversation flows
              </p>
            </div>
            <div className="flex items-center gap-2">
              
              {/* Flow Limit Info */}
              {!checkingAccess && flowAccess && !flowAccess.canCreate && (
                <div className="relative group">
                  <div className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertCircle size={18} />
                    <span>Flow Limit Reached</span>
                    <Info size={16} className="cursor-help" />
                  </div>
                  {/* Tooltip */}
                  <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    <p className="font-bold mb-1">Flow Limit Reached</p>
                    <p>You've used {flowAccess.currentFlows} of {flowAccess.totalAvailableFlows} flows.</p>
                    <p className="mt-2">Purchase add-on to create more flows.</p>
                  </div>
                </div>
              )}

              {/* Add-on Button */}
              {!checkingAccess && flowAccess && !flowAccess.canCreate && (
                <motion.button
                  onClick={() => setShowAddonModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl transition-all shadow-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ShoppingCart size={18} />
                  Add More Flows
                </motion.button>
              )}

              {/* Create Flow Button */}
              <motion.button
                onClick={() => {
                  if (!checkingAccess && flowAccess && !flowAccess.canCreate) {
                    toast.warning(`Flow limit reached. You have ${flowAccess.currentFlows} of ${flowAccess.totalAvailableFlows} flows. Purchase add-on to create more.`);
                    return;
                  }
                  navigate("/flow-builder");
                }}
                disabled={!checkingAccess && flowAccess && !flowAccess.canCreate}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm border ${
                  !checkingAccess && flowAccess && !flowAccess.canCreate
                    ? "bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed"
                    : "text-gray-900 hover:shadow-md border-gray-300"
                }`}
                style={!checkingAccess && flowAccess && !flowAccess.canCreate ? {} : { backgroundColor: "#eefa94" }}
                whileHover={!checkingAccess && flowAccess && !flowAccess.canCreate ? {} : { scale: 1.02 }}
                whileTap={!checkingAccess && flowAccess && !flowAccess.canCreate ? {} : { scale: 0.98 }}
                title={!checkingAccess && flowAccess && !flowAccess.canCreate ? `Flow limit reached (${flowAccess.currentFlows}/${flowAccess.totalAvailableFlows})` : "Create new flow"}
              >
                <Plus size={18} />
                Create Flow
              </motion.button>
            </div>
          </div>

          {/* Stats Cards */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="bg-white rounded-2xl border border-gray-200 p-4"
              variants={itemVariants}
              whileHover={{ scale: 1.01, y: -2 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    Total Flows
                  </p>
                  <motion.p
                    className="text-3xl font-bold text-gray-900"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 420 }}
                  >
                    {flows.length}
                  </motion.p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <MessageSquare size={24} className="text-blue-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl border border-gray-200 p-4"
              variants={itemVariants}
              whileHover={{ scale: 1.01, y: -2 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    Active Flows
                  </p>
                  <motion.p
                    className="text-3xl font-bold text-emerald-600"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.25, type: "spring", stiffness: 420 }}
                  >
                    {flows.filter((f) => f.is_active).length}
                  </motion.p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <CheckCircle2 size={24} className="text-emerald-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl border border-gray-200 p-4"
              variants={itemVariants}
              whileHover={{ scale: 1.01, y: -2 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">
                    Inactive Flows
                  </p>
                  <motion.p
                    className="text-3xl font-bold text-amber-600"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 420 }}
                  >
                    {flows.filter((f) => !f.is_active).length}
                  </motion.p>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <XCircle size={24} className="text-amber-600" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Tab Navigation */}
        <motion.div
          className="bg-white rounded-2xl border border-gray-200 p-2 shadow-sm mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.5 }}
        >
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("manage")}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                activeTab === "manage"
                  ? "text-gray-900 shadow-sm border border-gray-300"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
              style={activeTab === "manage" ? { backgroundColor: "#eefa94" } : {}}
            >
              <MessageSquare size={16} />
              Manage Flows
            </button>
            <button
              onClick={() => setActiveTab("allocate")}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                activeTab === "allocate"
                  ? "text-gray-900 shadow-sm border border-gray-300"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
              style={activeTab === "allocate" ? { backgroundColor: "#eefa94" } : {}}
            >
              <Link2 size={16} />
              Allocate Flows
            </button>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "manage" ? (
            <motion.div
              key="manage"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Search and Filter Bar */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm mb-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Search flows by name or description..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-400 focus:outline-none transition bg-white"
                    />
                  </div>
                  <div className="flex gap-2 bg-gray-50 rounded-xl p-1.5 border border-gray-200">
                    {["all", "active", "inactive"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setFilter(tab)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                          filter === tab
                            ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Flows Grid */}
              <AnimatePresence>
                {loading ? (
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm animate-pulse"
                      >
                        <div className="h-4 bg-gray-200 rounded mb-3"></div>
                        <div className="h-8 bg-gray-200 rounded mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    ))}\n                  </motion.div>
                ) : filteredFlows.length === 0 ? (
                  <motion.div
                    className="text-center py-16"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="bg-white rounded-2xl border border-gray-200 p-12 max-w-lg mx-auto shadow-sm">
                      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-200">
                        <MessageSquare size={32} className="text-gray-400" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {searchQuery ? "No flows found" : "Get Started Today"}
                      </h3>
                      <p className="text-sm text-gray-600 mb-6 font-medium leading-relaxed">
                        {searchQuery
                          ? `No flows match "${searchQuery}". Try a different search term.`
                          : "Create your first chatbot flow to automate conversations and engage with your customers"}
                      </p>
                      {!searchQuery && (
                        <motion.button
                          onClick={() => {
                            if (!checkingAccess && flowAccess && !flowAccess.canCreate) {
                              toast.warning(`Flow limit reached. Purchase add-on to create more flows.`);
                              return;
                            }
                            navigate("/flow-builder");
                          }}
                          disabled={!checkingAccess && flowAccess && !flowAccess.canCreate}
                          className={`px-6 py-3 text-sm font-bold rounded-xl transition-all shadow-md border inline-flex items-center gap-2 ${
                            !checkingAccess && flowAccess && !flowAccess.canCreate
                              ? "bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed"
                              : "text-gray-900 hover:shadow-lg border-gray-300"
                          }`}
                          style={!checkingAccess && flowAccess && !flowAccess.canCreate ? {} : { backgroundColor: "#eefa94" }}
                          whileHover={!checkingAccess && flowAccess && !flowAccess.canCreate ? {} : { scale: 1.03, y: -2 }}
                          whileTap={!checkingAccess && flowAccess && !flowAccess.canCreate ? {} : { scale: 0.97 }}
                        >
                          <Plus size={18} />
                          Create Your First Flow
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {filteredFlows.map((flow, index) => (
                      <motion.div
                        key={flow.id}
                        variants={itemVariants}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.06, duration: 0.45 }}
                      >
                        <FlowCard
                          flow={flow}
                          onEdit={() => navigate(`/flows/builder/${flow.id}`)}
                          onView={() => navigate(`/flows/builder/${flow.id}`)}
                          onDelete={() => handleDelete(flow.id)}
                          onToggleStatus={() =>
                            handleToggleStatus(flow.id, flow.is_active)
                          }
                          getTriggerBadge={getTriggerBadge}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              key="allocate"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <AllocateFlowsTab
                selectedPhoneAccount={selectedPhoneAccount}
                allocatedFlows={allocatedFlows}
                loading={allocatedLoading}
                unallocatedFlows={unallocatedFlows}
                showAddDropdown={showAddFlowDropdown}
                setShowAddDropdown={setShowAddFlowDropdown}
                onAllocate={handleAllocateFlow}
                onDeallocate={handleDeallocateFlow}
                allocatingFlowId={allocatingFlowId}
                deallocatingFlowId={deallocatingFlowId}
                getTriggerBadge={getTriggerBadge}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {aiModalOpen && (
        <AiChatbotModal
          onClose={() => setAiModalOpen(false)}
          loading={aiListLoading}
          phoneOptions={aiPhoneOptions}
          selectedPhoneId={selectedPhoneId}
          onSelectPhone={handleSelectPhone}
          aiEnabled={!!selectedPhone?.ai_enabled}
          onToggleAi={handleAiToggle}
          toggleLoading={aiToggleLoading}
          knowledgeDraft={aiKnowledgeDraft}
          onKnowledgeChange={setAiKnowledgeDraft}
          toneDraft={toneDraft}
          onToneChange={setToneDraft}
          outOfContextDraft={outOfContextDraft}
          onOutOfContextChange={setOutOfContextDraft}
          languageDraft={languageDraft}
          onLanguageChange={setLanguageDraft}
          roleDraft={roleDraft}
          onRoleChange={setRoleDraft}
          tasksAndRulesDraft={tasksAndRulesDraft}
          onTasksAndRulesChange={setTasksAndRulesDraft}
          aiReplyDays={aiReplyDays}
          aiReplyStartTime={aiReplyStartTime}
          aiReplyEndTime={aiReplyEndTime}
          aiTimezone={aiTimezone}
          onScheduleChange={(data) => {
            setAiReplyDays(data.ai_reply_days);
            setAiReplyStartTime(data.ai_reply_start_time);
            setAiReplyEndTime(data.ai_reply_end_time);
            setAiTimezone(data.ai_timezone);
          }}
          aiReplyToButtons={aiReplyToButtons}
          onAiReplyToButtonsChange={setAiReplyToButtons}
          aiReplyToAll={aiReplyToAll}
          onAiReplyToAllChange={setAiReplyToAll}
          aiButtonTexts={aiButtonTexts}
          onAiButtonTextsChange={setAiButtonTexts}
          onSaveAll={handleSaveAllSettings}
          saving={knowledgeSaving}
        />
      )}

      {/* Add-on Modal */}
      <AddonModal
        isOpen={showAddonModal}
        onClose={() => setShowAddonModal(false)}
        onSuccess={() => {
          setShowAddonModal(false);
          checkFlowAccessStatus();
          fetchFlows();
          toast.success("Additional flows added successfully!");
        }}
      />
    </div>
  );
};

// Allocate Flows Tab Component - uses PhoneSelector at page level
const AllocateFlowsTab = ({
  selectedPhoneAccount,
  allocatedFlows,
  loading,
  unallocatedFlows,
  showAddDropdown,
  setShowAddDropdown,
  onAllocate,
  onDeallocate,
  allocatingFlowId,
  deallocatingFlowId,
  getTriggerBadge,
}) => {
  const [searchFlow, setSearchFlow] = useState("");

  const filteredUnallocatedFlows = unallocatedFlows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchFlow.toLowerCase()) ||
      (flow.description &&
        flow.description.toLowerCase().includes(searchFlow.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Allocated Flows Section */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-gray-900">
                Allocated Flows
              </h3>
              <p className="text-xs text-gray-600 mt-1 font-medium">
                {selectedPhoneAccount
                  ? `Flows assigned to ${
                      selectedPhoneAccount.phoneName || selectedPhoneAccount.phoneNumber
                    }`
                  : "Select a phone above to view allocated flows"}
              </p>
            </div>

            {/* Add Flow Button */}
            <div className="relative">
              <motion.button
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-gray-900 rounded-xl transition-all shadow-sm hover:shadow-md border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={!selectedPhoneAccount?.id}
              >
                <Plus size={16} />
                Add Flow
                <ChevronDown
                  size={16}
                  className={`transition-transform ${
                    showAddDropdown ? "rotate-180" : ""
                  }`}
                />
              </motion.button>

              {/* Add Flow Dropdown */}
              <AnimatePresence>
                {showAddDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowAddDropdown(false)}
                    />
                    <motion.div
                      className="absolute right-0 top-12 bg-white rounded-xl shadow-xl border border-gray-200 z-20 w-80 overflow-hidden"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.15 }}
                    >
                      {/* Search Input */}
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <Search
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            size={16}
                          />
                          <input
                            type="text"
                            placeholder="Search flows..."
                            value={searchFlow}
                            onChange={(e) => setSearchFlow(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-400 focus:outline-none transition bg-white"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Flow List */}
                      <div className="max-h-64 overflow-y-auto">
                        {filteredUnallocatedFlows.length === 0 ? (
                          <div className="p-6 text-center">
                            <p className="text-sm text-gray-500 font-medium">
                              {unallocatedFlows.length === 0
                                ? "All flows are already allocated"
                                : "No flows match your search"}
                            </p>
                          </div>
                        ) : (
                          filteredUnallocatedFlows.map((flow) => (
                            <button
                              key={flow.id}
                              onClick={() => onAllocate(flow.id)}
                              disabled={allocatingFlowId === flow.id}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between gap-3 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-gray-900 truncate">
                                  {flow.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate font-medium">
                                  {flow.description || "No description"}
                                </p>
                              </div>
                              {allocatingFlowId === flow.id ? (
                                <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Link2
                                  size={16}
                                  className="text-gray-400 flex-shrink-0"
                                />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Allocated Flows List */}
        <div className="p-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="bg-gray-50 rounded-xl border border-gray-200 p-4 animate-pulse"
                >
                  <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : allocatedFlows.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-200">
                <Unlink size={32} className="text-gray-400" />
              </div>
              <p className="text-base font-bold text-gray-900 mb-1">
                No flows allocated
              </p>
              <p className="text-sm text-gray-600 font-medium">
                Click "Add Flow" to allocate flows to this phone
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {allocatedFlows.map((flow) => (
                <motion.div
                  key={flow.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  layout
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-bold text-gray-900 truncate">
                          {flow.name}
                        </h4>
                        <span
                          className={`flex-shrink-0 w-2 h-2 rounded-full ${
                            flow.is_active ? "bg-emerald-600" : "bg-gray-400"
                          }`}
                        />
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-1 mb-3 font-medium">
                        {flow.description || "No description"}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {getTriggerBadge(flow.trigger_type)}
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                            flow.is_active
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-gray-50 text-gray-600 border-gray-200"
                          }`}
                        >
                          {flow.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    <motion.button
                      onClick={() => onDeallocate(flow.id)}
                      disabled={deallocatingFlowId === flow.id}
                      className="flex-shrink-0 p-2 rounded-xl text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors border border-transparent hover:border-red-200"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Remove flow"
                    >
                      {deallocatingFlowId === flow.id ? (
                        <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <X size={18} />
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Flow Card Component
const FlowCard = ({
  flow,
  onEdit,
  onView,
  onDelete,
  onToggleStatus,
  getTriggerBadge,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden group cursor-pointer hover:border-gray-300"
      onClick={onView}
      whileHover={{
        scale: 1.01,
        y: -2,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
      transition={{ duration: 0.2 }}
    >
      <div
        className={`h-1 ${flow.is_active ? "bg-emerald-500" : "bg-gray-300"}`}
      ></div>

      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-base font-bold text-gray-900 truncate">
                {flow.name}
              </h3>
              <motion.span
                className={`flex-shrink-0 w-2 h-2 rounded-full ${
                  flow.is_active ? "bg-emerald-500" : "bg-gray-400"
                }`}
                animate={flow.is_active ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              ></motion.span>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem] font-medium leading-relaxed">
              {flow.description || "No description provided"}
            </p>
          </div>

          <div className="relative ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
            >
              <MoreVertical size={16} className="text-gray-500" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                  }}
                ></div>
                <motion.div
                  className="absolute right-0 top-10 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 z-20 min-w-[160px]"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                  >
                    <Eye size={14} />
                    View Flow
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                  >
                    <Edit size={14} />
                    Edit Flow
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleStatus();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                  >
                    {flow.is_active ? <Pause size={14} /> : <Play size={14} />}
                    {flow.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                  >
                    <Trash2 size={14} />
                    Delete Flow
                  </button>
                </motion.div>
              </>
            )}
          </div>
        </div>

        <div className="mb-3">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {getTriggerBadge(flow.trigger_type)}
            <span
              className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                flow.is_active && !flow.is_expired
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : flow.is_expired
                  ? "bg-red-50 text-red-700 border-red-200"
                  : "bg-gray-50 text-gray-600 border-gray-200"
              }`}
            >
              {flow.is_expired 
                ? "Expired" 
                : flow.is_active 
                ? "Active" 
                : "Inactive"}
            </span>
            
            {/* Expiration warning badge */}
            {flow.expires_at && !flow.is_expired && flow.days_until_expiry !== null && flow.days_until_expiry <= 7 && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                {flow.days_until_expiry <= 0
                  ? "Expires today"
                  : flow.days_until_expiry === 1
                  ? "Expires tomorrow"
                  : `${flow.days_until_expiry} days left`}
              </span>
            )}
          </div>
          {flow.trigger_type === "keyword" &&
            flow.trigger_keywords?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {flow.trigger_keywords.length === 1 && flow.trigger_keywords[0] === "__any__" ? (
                  <span className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-bold border border-green-200">
                    Any message
                  </span>
                ) : (
                  <>
                    {flow.trigger_keywords.slice(0, 3).map((keyword, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full font-bold border border-blue-200"
                      >
                        {keyword}
                      </span>
                    ))}
                    {flow.trigger_keywords.length > 3 && (
                      <span className="text-xs text-gray-500 px-2 py-1 font-semibold">
                        +{flow.trigger_keywords.length - 3} more
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
        </div>

        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3 pb-3 border-t border-gray-100 pt-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Calendar size={12} />
            <span className="font-semibold">
              {new Date(flow.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
          
          {/* Expiration Info */}
          {flow.expires_at && (
            <div className="flex items-center gap-1.5">
              {flow.is_expired ? (
                <>
                  <AlertCircle size={12} className="text-red-500" />
                  <span className="font-semibold text-red-600">
                    Expired
                  </span>
                </>
              ) : flow.days_until_expiry !== null ? (
                <>
                  <Clock size={12} className={flow.days_until_expiry <= 7 ? "text-amber-500" : "text-gray-500"} />
                  <span className={`font-semibold ${flow.days_until_expiry <= 7 ? "text-amber-600" : "text-gray-600"}`}>
                    {flow.days_until_expiry <= 0
                      ? "Expires today"
                      : flow.days_until_expiry === 1
                      ? "Expires in 1 day"
                      : `Expires in ${flow.days_until_expiry} days`}
                  </span>
                </>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl transition-colors font-bold text-sm border border-blue-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Eye size={14} />
            View
          </motion.button>
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors font-bold text-sm border border-gray-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Edit size={14} />
            Edit
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default FlowList;
